import { useState } from "react";
import { router } from "@inertiajs/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Download } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  importPath: string;
  title: string;
  description: string;
  // Sentence describing the cell values, e.g. "whole units; thousands
  // separators are fine; ..." — everything else about the file format
  // (columns, blank-cell semantics, re-import safety) is shared.
  valuesHint: string;
  // Which rows the importer reads, by the `type` column ("qty" or
  // "revenue"). The exported file carries both rows per product; the
  // importer reads its own and ignores the other's.
  rowType: "qty" | "revenue";
  // ISO first-of-month dates the importer will write (plan months that are
  // not past and not before the version's plan period). The example header
  // and the downloadable example file list exactly these.
  writableMonths: string[];
  // Used in the example file's name, e.g. "aop-us-2026-shopify".
  fileStem: string;
}

interface FormatHintProps {
  valuesHint: string;
  rowType: "qty" | "revenue";
  writableMonths: string[];
  fileStem: string;
}

// "2026-10-01" -> "10/26", the MM/YY form MonthImportBase parses.
function toMmYy(iso: string): string {
  return `${iso.slice(5, 7)}/${iso.slice(2, 4)}`;
}

function FormatHint({ valuesHint, rowType, writableMonths, fileStem }: FormatHintProps) {
  const otherType = rowType === "qty" ? "revenue" : "qty";
  const months = writableMonths.map(toMmYy);
  // Same shape as Export CSV: one sample product with its qty and revenue
  // rows. The user replaces the sample with their own products.
  const exampleHeader = [ "Product ID", "SKU", "Product Name", "Short Name", "Plan Node", "ASP", "type", ...months ].join(",");
  const sample = (type: string, values: string[]) =>
    [ "13984", "HL-SERUM", "Hada Labo Serum", "Serum", "Hada Labo > Skincare", "6290000", type, ...values ].join(",");
  const exampleLines = [
    exampleHeader,
    sample("qty", months.map((_, i) => String(5 + i))),
    sample("revenue", months.map((_, i) => String((5 + i) * 6290000))),
  ];
  const exampleFileHref = `data:text/csv;charset=utf-8,${encodeURIComponent(`${exampleLines.join("\n")}\n`)}`;
  const exampleFileName = `${fileStem}-${rowType}-example.csv`;

  const bullets: [string, string][] = [
    [
      "CSV only",
      ' — export from Excel or Google Sheets via "Save as CSV". Excel files (.xlsx) are rejected.',
    ],
    [
      "Required columns:",
      ` product id (any header containing "produc", e.g., "Product ID"), a "type" column, and one per month (MM/YY, e.g., ${months[0] ?? "01/26"}). Other columns (SKU, name, Plan Node, ASP) are ignored.`,
    ],
    [
      "Rows:",
      ` only rows whose type is "${rowType}" are read; "${otherType}" rows are ignored${rowType === "qty" ? " (a blank type counts as qty)" : ""}.`,
    ],
    [ "Values:", ` ${valuesHint}` ],
    [
      "Months outside the plan",
      " are skipped with a warning.",
    ],
    [
      "Re-import is safe:",
      " existing cells are updated, not duplicated. Tip: use Export CSV as your template.",
    ],
  ];

  return (
    <div className="min-w-0 rounded-md border bg-muted px-3 py-2 text-xs">
      <p className="mb-2 font-medium text-foreground">File format</p>

      <ul className="mb-2 space-y-1 text-muted-foreground">
        {bullets.map(([strong, rest]) => (
          <li key={strong} className="flex gap-2">
            <span className="flex-shrink-0">•</span>
            <span>
              <strong className="font-medium">{strong}</strong>
              {rest}
            </span>
          </li>
        ))}
      </ul>

      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="font-medium text-foreground">Example:</p>
        <a
          href={exampleFileHref}
          download={exampleFileName}
          className="inline-flex items-center gap-1 text-link hover:underline"
        >
          <Download className="h-3 w-3" />
          Download example file
        </a>
      </div>
      <div className="overflow-x-auto rounded-md border bg-background p-2">
        <code className="whitespace-pre font-mono text-xs text-muted-foreground">
          {exampleLines.join("\n")}
        </code>
      </div>
    </div>
  );
}

export default function ImportCsvDialog({
  open,
  onClose,
  importPath,
  title,
  description,
  valuesHint,
  rowType,
  writableMonths,
  fileStem,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function handleOpenChange(next: boolean) {
    if (!next && !submitting) {
      setFile(null);
      onClose();
    }
  }

  function submit() {
    if (!file) return;
    setSubmitting(true);
    const data = new FormData();
    data.append("file", file);
    router.post(importPath, data, {
      forceFormData: true,
      onSuccess: () => {
        setFile(null);
        onClose();
      },
      onFinish: () => setSubmitting(false),
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <FormatHint
          valuesHint={valuesHint}
          rowType={rowType}
          writableMonths={writableMonths}
          fileStem={fileStem}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="by-month-csv" className="text-sm font-medium">
            CSV file
          </label>
          <input
            id="by-month-csv"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={submitting}
            className="text-[13px] file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-[12px] file:font-medium file:text-foreground hover:file:bg-accent"
          />
          {file && (
            <span className="text-[11px] text-muted-foreground">
              Selected: {file.name} ({Math.round(file.size / 1024)} KB)
            </span>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={!file || submitting}>
            {submitting ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
